import React, { useState, useEffect, useContext, useRef } from 'react';
import { PencilSquareIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import ListBox from './list_box';
import { getHolidaysCompanyList, addHolidayCompany, deleteHolidayCompany, updateHolidayCompany } from '../services/api_holiday';
import { AuthContext } from '../contexts/auth';
import Swal from 'sweetalert2';


const FestivitaTable = ({ showForm, setShowForm }) => {
    const { loading, token } = useContext(AuthContext);
    const [festivita, setFestivita] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [holidayName, setHolidayName] = useState('');
    const [selectedMonth, setSelectedMonth] = useState({ name: '', value: '' });
    const [selectedDay, setSelectedDay] = useState({ name: '', value: '' });    
    const [editingHoliday, setEditingHoliday] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [operation, setOperation] = useState('add');
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const formRef = useRef(null);

    useEffect(() => {
        const fetchHolidays = async () => {
            setIsLoading(true);
            setError(null);
        
            try {
                const holidaysData = await getHolidaysCompanyList(token);
                setFestivita(holidaysData);
            } catch (error) {
                setError(error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchHolidays();
    }, [token]);

    const handleAddNew = () => {
        setOperation('add');
        setHolidayName('');
        setSelectedDay({ name: '', value: '' });
        setSelectedMonth({ name: '', value: '' });
        setEditingHoliday(null);
        setIsEditing(false);
        setShowForm(true);
    };    

    const handleCancel = () => {
        setHolidayName('');
        setSelectedDay({ name: '', value: '' });
        setSelectedMonth({ name: '', value: '' });
        setShowForm(false);
        setIsEditing(false);
    };    

    const handleSaveHoliday = async (e) => {
        e.preventDefault();
    
        if (!holidayName.trim() || !selectedDay.value || !selectedMonth.value) {
            Swal.fire({
                icon: 'warning',
                text: 'Per proseguire termina la compilazione dei campi.',
            });
            return;
        }
    
        try {
            const checkDay = parseInt(selectedDay.value.toString());
            const checkMonth = parseInt(selectedMonth.value.toString());

            setIsSaving(true);

            if (operation === 'add') {
                await addHolidayCompany(token, holidayName, checkDay, checkMonth);
                Swal.fire({
                    title: "Festività creata con successo!",
                    icon: "success",
                });
                setHolidayName('');
                setSelectedDay({ name: '', value: '' });
                setSelectedMonth({ name: '', value: '' });
            } else if (operation === 'edit' && editingHoliday) {
                await updateHolidayCompany(token, editingHoliday.id, holidayName, checkDay, checkMonth);
                Swal.fire({
                    title: "Festività modificata con successo!",
                    icon: "success"
                });
                setHolidayName('');
                setSelectedDay({ name: '', value: '' });
                setSelectedMonth({ name: '', value: '' });
                setEditingHoliday(null);
                setIsEditing(false);
            }
    
            const updatedHolidays = await getHolidaysCompanyList(token);
            setFestivita(updatedHolidays);
            setShowForm(false);
        } catch (error) {
            console.error('Error saving holiday:', error);
            Swal.fire({
                icon: "error",
                title: "Oops...",
                text: "Operazione non eseguita",
            });
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleShowMessage = async (holidayId) => {
        Swal.fire({
            icon: "question",
            title: `Sei sicuro di voler procedere con l'eliminazione?`,
            showCancelButton: true,
            confirmButtonText: "Sì",
            cancelButtonText: "Annulla",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await deleteHolidayCompany(token, holidayId.toString());
                    const updatedHolidays = festivita.filter(holiday => holiday.id !== holidayId);
                    setFestivita(updatedHolidays);
                    setShowModal(false);
                    Swal.fire({
                        title: 'Festività eliminata con successo!',
                        icon: "success",
                        timer: 1500,
                        showConfirmButton: false,
                    });
                } catch (error) {
                    console.error('Error deleting holiday:', error);
                    Swal.fire({
                        icon: "error",
                        title: "Oops...",
                        text: "Eliminazione non eseguita",
                    });
                }
            }
        });
    
        setShowModal(true);
    };
    

    const handleEditHoliday = (holiday) => {
        scrollToForm()
        if (isEditing) {
            Swal.fire({
                icon: 'warning',
                title: 'Stai già modificando una festività',
                text: "Termina l'operazione oppure annulla per proseguire con un'altra operazione",
            });
            return;
        }

        setOperation('edit');
        setEditingHoliday(holiday);
        setIsEditing(true);
        setShowForm(true);
        setHolidayName(holiday.name);
        setSelectedDay({ name: holiday.day.toString(), value: holiday.day });
        setSelectedMonth({ name: months[holiday.month - 1], value: holiday.month });
    };
    
    const scrollToForm = () => {
        if (formRef.current) {
            formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    useEffect(() => {
        if (showForm) {
            scrollToForm();
        }
    }, [showForm]);

    const months = [
        'Gennaio',
        'Febbraio',
        'Marzo',
        'Aprile',
        'Maggio',
        'Giugno',
        'Luglio',
        'Agosto',
        'Settembre',
        'Ottobre',
        'Novembre',
        'Dicembre'
    ];

    return (
        <div>
            <div className="flex justify-end mb-5">
                <button
                    className="border flex items-center rounded-md bg-grey-400 px-3 py-2 text-center text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 w-40"
                    onClick={handleAddNew}
                >
                    <PlusIcon className="w-4 h-4 mr-1" />
                    Aggiungi Nuova
                </button>
            </div>
            {showForm && (
                <div ref={formRef} id="holidayForm" className="rounded border border-grey-400 p-4 mb-8 overflow-x-auto">
                    {operation === 'add' ? (
                        <h1 className="text-sm font-semibold leading-6 text-gray-900">CREA NUOVA FESTIVITÀ</h1>
                    ) : (
                        <h1 className="text-sm font-semibold leading-6 text-gray-900">MODIFICA FESTIVITÀ</h1>
                    )}
                    <form onSubmit={handleSaveHoliday}>
                        <div className="grid md:grid-cols-3 sm:grid-cols-1 gap-7 mt-4">
                            <div>
                                <label htmlFor="nome" className="block text-sm font-medium text-gray-900">Denominazione</label>
                                <input
                                    type="text"
                                    id="nome"
                                    name="Denominazione"
                                    value={holidayName}
                                    onChange={(e) => setHolidayName(e.target.value)}
                                    className="block w-full mt-1 rounded-md border-gray-300 shadow-sm placeholder:text-gray-400 placeholder:text-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
                                    placeholder="Inserisci denominazione"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="day" className="block text-sm font-medium text-gray-900">Giorno</label>
                                <ListBox
                                    options={Array.from({ length: 31 }, (_, i) => ({ name: (i + 1).toString(), value: i + 1 }))}
                                    onChange={(selected) => setSelectedDay(selected)}
                                    required={true}
                                    placeholder={{ name: 'Seleziona giorno', value: '' }}
                                    defaultValue={isEditing ? selectedDay : undefined}
                                    positionOptions='relative'
                                />
                            </div>
                            <div className='mb-0'>
                                <label htmlFor="month" className="block text-sm font-medium text-gray-900">Mese</label>
                                <ListBox
                                    options={months.map((month, index) => ({ name: month, value: index + 1 }))}
                                    onChange={(selected) => setSelectedMonth(selected)}
                                    required={true}
                                    placeholder={{ name: 'Seleziona mese', value: '' }}
                                    defaultValue={isEditing ? selectedMonth : undefined}
                                    positionOptions='relative'
                                />
                            </div>
                        </div>
                        <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-7 mt-4">
                            <div className="flex justify-center md:justify-end">
                                <button
                                    type="submit"
                                    className={`border border-indigo-400 flex items-center rounded-md px-5 py-2 text-center text-sm font-semibold text-white shadow-sm focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 w-24 justify-center mr-3 bg-indigo-600  ${
                                        isSaving ? 'bg-gray-400 cursor-not-allowed text-gray-400' : 'bg-grey-400 hover:bg-indigo-400 hover:text-white'
                                    }`}
                                    onClick={handleSaveHoliday}
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Salvataggio...' : 'Salva'}
                                </button>
                            </div>
                            <div className="flex justify-center md:justify-start">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="border border-gray-400 block rounded-md bg-grey-400 px-5 py-2 mr-2 text-center text-sm font-semibold text-grey-800 shadow-sm hover:bg-grey-500 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 hover:bg-gray-400 hover:text-white w-24 justify-center"
                                >
                                    Annulla
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            )}
            
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col-4" className="px-4 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Denominazione</th>
                            <th scope="col-2" className="px-4 py-3 text-center text-xs font-medium text-gray-900 uppercase tracking-wider">Giorno</th>
                            <th scope="col-2" className="px-4 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Mese</th>
                            <th scope="col-2" className="px-4 py-3 text-center text-xs font-medium text-gray-900 uppercase tracking-wider">Azioni</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {festivita
                            .sort((a, b) => {
                                if (a.month !== b.month) {
                                    return a.month - b.month;
                                }
                                return a.day - b.day;
                            }).map(holiday => (
                            <tr key={holiday.id}>
                                <td className="px-4 py-1 whitespace-nowrap text-gray-500 text-base">{holiday.name}</td>
                                <td className="px-4 py-1 whitespace-nowrap text-gray-500 text-base text-center">{holiday.day}</td>
                                <td className="px-4 py-1 whitespace-nowrap text-gray-500 text-base text-left">{months[holiday.month - 1]}</td>
                                <td className="px-4 py-2 text-gray-500 flex justify-center align-center">
                                    <button className="mr-2 text-gray-700 font-bold px-4" title="Modifica" onClick={() => handleEditHoliday(holiday)}>
                                        <PencilSquareIcon className="h-6 w-6 hover:text-gray-400" /> 
                                    </button>
                                    <button 
                                        className="text-gray-700 font-bold px-4" 
                                        title="Elimina"
                                        onClick={() => handleShowMessage(holiday.id)}
                                    >
                                        <TrashIcon className="h-6 w-6 hover:text-red-600" /> 
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default FestivitaTable;
